import { useEffect, useMemo, useState } from "react";
import {
  Button,
  CardNotification,
  Heading,
  Loading,
  Menu,
  RoundButton,
  Table,
  Tag,
  Text,
  toast,
  Pill,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  ProgrammingStructurePart,
  ProgrammingStructureValidationError,
  ProgrammingStructureValidationReason,
  UpdateProgrammingStructureInput,
} from "@models/ISchema";
import { getColumnCell } from "@common/utils/table.util";
import ProgrammingStructuresLayout from "@modules/programming-structures/layout/programming-structures-layout";
import ProgrammingStructuresForm from "@modules/programming-structures/components/programming-structures-form";
import useContextUrl from "@common/hooks/useContextUrl";
import StagesDrawer from "@modules/programming-structures/components/stages-drawer";
import {
  useCreateProgrammingStructure,
  useGetProgrammingStructureById,
  useUpdateProgrammingStructure,
} from "@services/collections/programming-structures/programmingStructuresServices";
import { useProgrammingStructuresPermissions } from "@modules/programming-structures/hooks/useProgrammingStructuresPermissions";
import PublishStructureModal from "@modules/programming-structures/components/publish-structure-modal";
import AddCampusesConfirmModal from "@modules/programming-structures/components/add-campuses-confirm-modal";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import UnsavedChangesModal from "@common/components/unsaved-changes-modal";
import DuplicatedStructureModal from "@modules/programming-structures/components/duplicated-structure-modal";
import styles from "./programmingStructuresCreation.module.scss";

const ProgrammingStructuresCreation = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { scenario, origin, id } = useContextUrl();
  const { getProgrammingStructuresUrl } = useNavigationUrl();
  const [isValidForm, setIsValidForm] = useState(false);
  const [structureParts, setStructureParts] = useState<ProgrammingStructurePart[]>([]);
  const [showStageDrawer, setShowStageDrawer] = useState(false);
  const [selectedStructurePart, setSelectedStructurePart] =
    useState<ProgrammingStructurePart | null>(null);
  const [formState, setFormState] = useState<{
    code: string;
    name: string;
    subject: any;
    campuses: any[];
  }>({
    code: "",
    name: "",
    subject: null,
    campuses: [],
  });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showAddCampusesModal, setShowAddCampusesModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showDuplicatedErrorModal, setShowDuplicatedErrorModal] = useState(false);

  const copyFromId = useMemo(() => {
    const params = new URLSearchParams(history?.location?.search || "");
    return params.get("copyFrom");
  }, [history?.location?.search]);

  const { createProgrammingStructure, loading } = useCreateProgrammingStructure();

  const { updateProgrammingStructure, loading: updatingStructure } =
    useUpdateProgrammingStructure();

  const {
    getProgrammingStructureById,
    loading: loadingStructure,
    programmingStructure,
  } = useGetProgrammingStructureById();

  const { permissions } = useProgrammingStructuresPermissions();

  const currentPath = useMemo(() => {
    if (history?.location?.pathname.includes("new")) {
      return "create";
    }

    if (history?.location?.pathname.includes("update")) {
      return "update";
    }

    return "details";
  }, [history?.location?.pathname]);

  const canEditOrCreateStructure = useMemo(() => {
    const isCreationPath = currentPath === "create";
    const isEditingPath = currentPath === "update";
    const isDetailsPath = currentPath === "details";

    const isPublished = programmingStructure?.programmingStructureStats?.isPublished;

    if (isCreationPath && !permissions?.create) {
      return false;
    }

    if (isEditingPath && !permissions?.update) {
      return false;
    }

    if (!copyFromId && programmingStructure && isPublished) {
      return false;
    }

    if (loadingStructure || isDetailsPath) {
      return false;
    }

    return true;
  }, [programmingStructure, copyFromId, loadingStructure, permissions, currentPath]);

  const isAddCampusesMode = useMemo(() => {
    const isPublished = programmingStructure?.programmingStructureStats?.isPublished;

    return currentPath === "details" && !!isPublished && !!permissions?.update;
  }, [currentPath, programmingStructure, permissions]);

  const hasAddedCampuses = useMemo(() => {
    if (!programmingStructure) return false;

    const baselineCampusCodes = programmingStructure.campusCodes ?? [];

    return (formState.campuses ?? []).some(code => !baselineCampusCodes.includes(code));
  }, [formState.campuses, programmingStructure]);

  const handleCreateProgrammingStructure = async (isPublished = false) => {
    if (loading || updatingStructure || loadingStructure) return;

    if (showPublishModal) {
      setShowPublishModal(false);
    }

    const getHasDuplicatedError = (
      validationErrors: ProgrammingStructureValidationError[] = [],
    ) => {
      return validationErrors?.some(
        error =>
          error?.reason ===
          ProgrammingStructureValidationReason.ProgrammingStructureDuplicateCodeSubject,
      );
    };

    const listPath = getProgrammingStructuresUrl();
    const payload = {
      code: formState.code,
      name: formState.name,
      subjectCodes: [formState.subject?.code],
      isPublished,
      isVisible: isPublished,
      campusCodes: formState.campuses,
      programmingStructureParts: structureParts?.map(part => ({
        id: part?.id?.includes("new-stage") || !!copyFromId ? undefined : part.id,
        code: part?.code,
        name: part?.name,
        blockDuration: part?.blockDuration,
        programmingStructureStageId: part?.programmingStructureStage.id,
        dependenciesCodes: part?.dependenciesCodes,
        references: part?.references,
      })),
    };

    // If the programming structure already exists, we update it, otherwise we create a new one
    if (programmingStructure && !copyFromId) {
      const { data: updatedData } = await updateProgrammingStructure({
        scenarioId: scenario,
        originId: origin,
        input: {
          dryRun: false,
          skipValidations: false,
          changesets: {
            updates: [
              {
                id: programmingStructure.id,
                ...(payload as UpdateProgrammingStructureInput),
              },
            ],
          },
        },
      });

      if (!updatedData?.cube?.programmingStructuresCrud?.commited) {
        updatedData?.cube?.programmingStructuresCrud?.payload?.updates?.forEach(update => {
          if (getHasDuplicatedError(update?.validationErrors)) {
            setShowDuplicatedErrorModal(true);
            return;
          }
        });
      } else {
        toast.success(t("programming-structures.notifications.toast.update-success"), {
          autoClose: 3000,
        });

        history.replace(listPath);
      }

      return;
    }

    const { data } = await createProgrammingStructure({
      scenarioId: scenario,
      originId: origin,
      input: {
        dryRun: false,
        skipValidations: false,
        changesets: {
          creates: [payload],
        },
      },
    });

    if (!data?.cube?.programmingStructuresCrud?.commited) {
      data?.cube?.programmingStructuresCrud?.payload?.creates?.forEach(create => {
        if (getHasDuplicatedError(create?.validationErrors)) {
          setShowDuplicatedErrorModal(true);
          return;
        }
      });
    } else {
      toast.success(t("programming-structures.notifications.toast.create-success"), {
        autoClose: 3000,
      });

      history.replace(listPath);
    }
  };

  const handleSaveAddedCampuses = async () => {
    if (updatingStructure || loadingStructure || !programmingStructure) return;

    const notifyError = () =>
      toast.error(t("programming-structures.notifications.toast.add-campuses-error"), {
        autoClose: 3000,
      });

    try {
      const { data } = await updateProgrammingStructure({
        scenarioId: scenario,
        originId: origin,
        input: {
          dryRun: false,
          skipValidations: false,
          changesets: {
            updates: [
              {
                id: programmingStructure.id,
                campusCodes: formState.campuses,
              },
            ],
          },
        },
      });

      if (!data?.cube?.programmingStructuresCrud?.commited) {
        notifyError();
        return;
      }

      toast.success(t("programming-structures.notifications.toast.add-campuses-success"), {
        autoClose: 3000,
      });

      getProgrammingStructureById(scenario, id);
    } catch {
      notifyError();
    } finally {
      setShowAddCampusesModal(false);
    }
  };

  const hasRequiredStages = useMemo(() => {
    const stagesByCode = structureParts.reduce(
      (acc, part) => {
        const stageTypeCode =
          part?.programmingStructureStage?.programmingStructureStageType?.code?.toUpperCase();

        acc[stageTypeCode] = part.programmingStructureStage.id;
        return acc;
      },
      {} as { [key: string]: string },
    );

    return {
      module: !!stagesByCode["MODULO"] || !!stagesByCode["MODULE"],
      challenge: !!stagesByCode["RETO"] || !!stagesByCode["CHALLENGE"],
    };
  }, [structureParts]);

  const handleCreateAndPublish = () => {
    if (!hasRequiredStages.module || !hasRequiredStages.challenge) {
      return;
    }

    handleCreateProgrammingStructure(true);
  };

  const isEditingAndHasChanges = useMemo(() => {
    if (!programmingStructure || !!copyFromId) return false;

    const hasFormChanges =
      formState.code !== programmingStructure.code ||
      formState.name !== programmingStructure.name ||
      formState.subject?.code !== programmingStructure.subjectCodes?.[0];

    const formCampusCodesByCode = formState?.campuses?.reduce(
      (acc, campus) => {
        acc[campus] = campus;
        return acc;
      },
      {} as { [key: string]: string },
    );

    const hasCampusesChanges = programmingStructure?.campusCodes?.some(
      code => !(code in formCampusCodesByCode),
    );

    const hasPartsChanges =
      JSON.stringify(structureParts) !==
      JSON.stringify(programmingStructure.programmingStructureParts);

    return hasFormChanges || hasCampusesChanges || hasPartsChanges;
  }, [formState, structureParts, programmingStructure]);

  const totalBlocks = useMemo(() => {
    if (!structureParts?.length) return 0;
    return structureParts.reduce((acc, part) => acc + (part?.blockDuration || 0), 0);
  }, [structureParts]);

  const handleGoBack = () => {
    const hasPendingChanges = isAddCampusesMode ? hasAddedCampuses : isEditingAndHasChanges;

    if (hasPendingChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }

    history.replace(getProgrammingStructuresUrl());
  };

  useEffect(() => {
    if ((id || copyFromId) && !programmingStructure && !loadingStructure) {
      getProgrammingStructureById(scenario, id || copyFromId);
    }
  }, [scenario, id]);

  useEffect(() => {
    if (programmingStructure) {
      setStructureParts(programmingStructure.programmingStructureParts);
    }
  }, [programmingStructure]);

  return (
    <ProgrammingStructuresLayout>
      {(loading || updatingStructure || loadingStructure) && <Loading />}

      <div className={styles.programmingStructuresCreation}>
        <header className={styles.programmingStructuresCreation_header}>
          <div className={styles.programmingStructuresCreation_title}>
            <RoundButton size="md" variant="ghost" icon="chevron-left" onClick={handleGoBack} />
            <Heading type="h2">
              {currentPath === "create" && !copyFromId && t("programming-structures.form.title")}
              {currentPath === "create" &&
                !!copyFromId &&
                t("programming-structures.form.title-from-copy")}
              {currentPath === "details" &&
                t("programming-structures.form.title-details", {
                  code: programmingStructure?.code,
                  name: programmingStructure?.name,
                })}
              {currentPath === "update" &&
                t("programming-structures.form.title-edit", {
                  code: programmingStructure?.code,
                  name: programmingStructure?.name,
                })}
            </Heading>
          </div>

          {canEditOrCreateStructure && (
            <div className={styles.programmingStructuresCreation_actions}>
              <Button variant="ghost" onClick={handleGoBack}>
                {t("generals.cancel-and-back")}
              </Button>
              <Button
                variant="secondary"
                disabled={
                  !isValidForm || (programmingStructure && !isEditingAndHasChanges && !copyFromId)
                }
                onClick={() => handleCreateProgrammingStructure()}
              >
                {t("generals.save-changes")}
              </Button>
              <Button
                variant="primary"
                leftIcon="upload"
                disabled={!isValidForm || !hasRequiredStages.module || !hasRequiredStages.challenge}
                onClick={() =>
                  programmingStructure ? setShowPublishModal(true) : handleCreateAndPublish()
                }
              >
                {t("programming-structures.actions.publish-structure")}
              </Button>
            </div>
          )}

          {isAddCampusesMode && (
            <div className={styles.programmingStructuresCreation_actions}>
              <Button variant="ghost" onClick={handleGoBack}>
                {t("generals.cancel-and-back")}
              </Button>
              <Button
                variant="secondary"
                disabled={!isValidForm || !hasAddedCampuses}
                onClick={() => setShowAddCampusesModal(true)}
              >
                {t("generals.save-changes")}
              </Button>
            </div>
          )}
        </header>

        <ProgrammingStructuresForm
          scenario={scenario}
          programmingStructure={programmingStructure}
          mode={isAddCampusesMode ? "campusesOnly" : canEditOrCreateStructure ? "edit" : "readOnly"}
          onChange={setFormState}
          onValidForm={setIsValidForm}
        />

        <section className={styles.programmingStructuresCreation_stages}>
          <div className={styles.programmingStructuresCreation_header}>
            <div className={styles.programmingStructuresCreation_stagesTitle}>
              <Heading type="h3">{t("programming-structures.form.stages-title")}</Heading>

              <Pill
                className={styles.programmingStructuresCreation_totalBlocksPill}
                color="neutral-30"
                textColor="neutral-70"
                outlined
              >
                <span className={styles.programmingStructuresCreation_totalBlocksText}>
                  {t("programming-structures.form.stages-blocks.title")}:
                </span>
                {t("programming-structures.form.stages-blocks.total-blocks", { totalBlocks })}
              </Pill>
            </div>

            {canEditOrCreateStructure && (
              <Button variant="secondary" leftIcon="plus" onClick={() => setShowStageDrawer(true)}>
                {t("programming-structures.actions.add-stage")}
              </Button>
            )}
          </div>

          {structureParts.length === 0 ? (
            <CardNotification
              title={t("programming-structures.notifications.cards.empty-stages.title")}
              state="info"
              outlined
            >
              {t("programming-structures.notifications.cards.empty-stages.description")}
            </CardNotification>
          ) : (
            <Table
              className={styles.programmingStructuresCreation_stagesTable}
              data={structureParts}
              columns={[
                {
                  key: "code",
                  header: t("programming-structures.form.stages-table.headers.code"),
                  renderCell: data => getColumnCell((data as ProgrammingStructurePart)?.code),
                },
                {
                  key: "name",
                  header: t("programming-structures.form.stages-table.headers.name"),
                  renderCell: data => getColumnCell((data as ProgrammingStructurePart)?.name),
                },
                {
                  key: "blockDuration",
                  header: t("programming-structures.form.stages-table.headers.duration"),
                  renderCell: data =>
                    getColumnCell((data as ProgrammingStructurePart)?.blockDuration),
                },
                {
                  key: "dependenciesCodes",
                  header: t("programming-structures.form.stages-table.headers.dependency"),
                  renderCell: data =>
                    getColumnCell(
                      (data as ProgrammingStructurePart)?.dependenciesCodes.join(", ") || "-",
                    ),
                },
                {
                  key: "references",
                  header: t("programming-structures.form.stages-table.headers.cip-codes"),
                  renderCell: data => (
                    <div className={styles.programmingStructuresCreation_cipCodesCell}>
                      {(data as ProgrammingStructurePart)?.references?.map(ref => (
                        <Tag key={ref} className={styles.programmingStructuresCreation_cipCodeTag}>
                          {ref}
                        </Tag>
                      ))}
                    </div>
                  ),
                },
              ]}
              rightActions={[
                {
                  renderContent: row => {
                    return (
                      <Menu
                        size="sm"
                        listOrigin="right"
                        disabled={!canEditOrCreateStructure}
                        variant="ghost"
                        items={[
                          {
                            label: t("programming-structures.actions.edit-stage"),
                            icon: "edit",
                            onClick: () => {
                              setSelectedStructurePart(row as ProgrammingStructurePart);
                              setShowStageDrawer(true);
                            },
                          },
                          {
                            label: t("programming-structures.actions.delete-stage"),
                            icon: "trash",
                            onClick: () => {
                              const filteredParts = structureParts
                                .filter(
                                  part => part.code !== (row as ProgrammingStructurePart).code,
                                )
                                .map(part => ({
                                  ...part,
                                  dependenciesCodes: part.dependenciesCodes.filter(
                                    code => code !== (row as ProgrammingStructurePart).code,
                                  ),
                                }));

                              setStructureParts([...filteredParts]);
                            },
                          },
                        ]}
                      />
                    );
                  },
                },
              ]}
            />
          )}

          {!!structureParts?.length &&
            (!hasRequiredStages?.module || !hasRequiredStages?.challenge) && (
              <CardNotification
                title={
                  !hasRequiredStages?.module && !hasRequiredStages?.challenge
                    ? t(
                        "programming-structures.notifications.cards.missing-stages.title-missing-both",
                      )
                    : !hasRequiredStages?.module
                      ? t(
                          "programming-structures.notifications.cards.missing-stages.title-missing-module",
                        )
                      : t(
                          "programming-structures.notifications.cards.missing-stages.title-missing-challenge",
                        )
                }
                state="warning"
                outlined
              >
                <Text type="sm">
                  {t("programming-structures.notifications.cards.missing-stages.description")}
                </Text>
              </CardNotification>
            )}
        </section>
      </div>

      <StagesDrawer
        isOpen={showStageDrawer}
        scenario={scenario}
        selectedStructurePart={selectedStructurePart}
        savedStructureParts={structureParts}
        onSaveStage={stage => {
          const isSaved = structureParts?.findIndex(part => part.code === stage.code);

          if (isSaved > -1) {
            const newStructureParts = [...structureParts];
            newStructureParts[isSaved] = stage;

            setStructureParts([...newStructureParts]);
            return;
          }

          setStructureParts([...structureParts, stage]);
        }}
        onClose={() => {
          setShowStageDrawer(false);
          setSelectedStructurePart(null);
        }}
      />

      <PublishStructureModal
        isOpen={showPublishModal}
        programmingStructure={programmingStructure}
        onClose={() => setShowPublishModal(false)}
        onPublish={handleCreateAndPublish}
      />

      <AddCampusesConfirmModal
        isOpen={showAddCampusesModal}
        isLoading={updatingStructure}
        onClose={() => setShowAddCampusesModal(false)}
        onConfirm={handleSaveAddedCampuses}
      />

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onConfirm={() => {
          setShowUnsavedChangesModal(false);
          history.replace(getProgrammingStructuresUrl());
        }}
      />

      <DuplicatedStructureModal
        isOpen={showDuplicatedErrorModal}
        onClose={() => setShowDuplicatedErrorModal(false)}
      />
    </ProgrammingStructuresLayout>
  );
};

export default ProgrammingStructuresCreation;
